import React, { PureComponent } from 'react'
import numeral from 'numeral'
import { FormattedMessage } from 'react-intl'
import { LearnMore } from 'src/constants/trackingEvents'
import { formatCurrency } from 'src/helpers/formatters'
import { QUESTIONS } from 'src/constants/messages'
import QuestionSingle from 'src/Components/homeowners/Questions/Single'
import { Button, If, Text } from 'src/Components'
import AdditionalInfo from 'src/Components/shared/AdditionalInfo'
import ChangeIndicator from 'src/Components/shared/ChangeIndicator'
import Panel from 'src/Components/shared/Panel'
import Info from './Info'
import EstimateAdjuster from './EstimateAdjuster'

import styles from './MortgageInsurance.module.scss'

const HIGH_LTV_FLOOR = 82
const LOW_LTV_CEIL = 80

class MortgageInsurance extends PureComponent {
  state = {
    open: false
  }

  toggleOpen = (open = !this.state.open) => {
    this.setState({ open })
  }

  getTagline () {
    const { ltvPercent } = this.props

    if (ltvPercent >= HIGH_LTV_FLOOR) {
      return (
        <FormattedMessage
          id='Homeowner.mortgageInsurange.tagline.ltvOver82'
          defaultMessage='Not yet.'
        />
      )
    }

    if (ltvPercent >= LOW_LTV_CEIL) {
      return (
        <FormattedMessage
          id='Homeowner.mortgageInsurance.tagline.ltvOver80'
          defaultMessage='Soon. Maybe now.'
        />
      )
    }

    return (
      <FormattedMessage
        id='Homeowner.mortgageInsurance.tagline.ltvUnder80'
        defaultMessage='Maybe now.'
      />
    )
  }

  renderTagline () {
    const { ltvPercent } = this.props

    return (
      <ChangeIndicator
        component={Text}
        className={styles.tagline}
        positiveIf={ltvPercent < HIGH_LTV_FLOOR}
        size='xl'
        weight='light'
      >
        {this.getTagline()}
      </ChangeIndicator>
    )
  }

  render () {
    const { avm, ltvPercent, estimatedPmi, manualPmi } = this.props
    const { open } = this.state

    return (
      <Panel data-qa='MortgageInsurance' className={styles.container}>
        <Panel.Heading>
          <If condition={manualPmi}>
            <FormattedMessage
              id='Homeowner.mortgageInsurance.headlineManualPMI'
              defaultMessage='When can you get rid of your {pmiTotal} monthly mortgage insurance?'
              values={{ pmiTotal: <em>{formatCurrency(manualPmi)}</em> }}
            />
          </If>
          <If condition={!manualPmi}>
            <FormattedMessage
              id='Homeowner.mortgageInsurance.headline'
              defaultMessage='When can you get rid of your estimated {pmiTotal} monthly mortgage insurance?'
              values={{ pmiTotal: <em>{formatCurrency(estimatedPmi)}</em> }}
            />
          </If>
        </Panel.Heading>
        {this.renderTagline()}
        <If condition={ltvPercent < HIGH_LTV_FLOOR}>
          <h2>
            <FormattedMessage
              id='Homeowner.mortgageInsurance.startCheckingMessage'
              defaultMessage="It's time to start checking."
            />
          </h2>
        </If>
        <EstimateAdjuster />
        <Text className={styles.explain}>
          <FormattedMessage
            id='Homeowner.mortgageInsurance.explainPMIDrop'
            defaultMessage='We estimate your Loan to Value ratio to be about {ltvPercent} based on the current estimated value of your home. You might be able to drop mortgage insurance if you meet certain conditions.'
            values={{
              ltvPercent: (
                <ChangeIndicator
                  component='em'
                  positiveIf={ltvPercent < HIGH_LTV_FLOOR}
                >
                  {numeral(ltvPercent).format('0[.]0')}%
                </ChangeIndicator>
              )
            }}
          />
        </Text>
        <Text className={styles.tellmemore}>
          <Button
            onClick={() => this.toggleOpen()}
            track={LearnMore}
            trackEnabled={!open}
            trackOptions={{ eventLabel: 'MortgageInsurance' }}
          >
            <FormattedMessage
              id='Homeowner.mortgageInsurance.toggleMoreInfoButton'
              defaultMessage='Tell me more'
            />
          </Button>
        </Text>
        <AdditionalInfo
          visible={open}
          cardWidth
          className={styles.additionalInfo}
        >
          <Info avm={avm} ltvPercent={ltvPercent} />
        </AdditionalInfo>
        <Panel.Footer>
          <QuestionSingle
            topic={QUESTIONS.mortgageInsuranceTopic}
            question={QUESTIONS.mortgageInsuranceFooter}
          />
        </Panel.Footer>
      </Panel>
    )
  }
}

export default MortgageInsurance
